import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import Icon from '@/components/AppIcon';
import { PageLayout } from '@/components/ui/PageLayout';
import { Switch } from '@/components/ui/Switch';
import { Spinner } from '@/components/ui/Spinner';
import { Laptop } from 'lucide-react';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import {
  getAssetSettings,
  updateAssetSettings,
} from '../../../../api/General/assets';
import AssetCategoriesTab from '../../../General/Assets/components/AssetCategoriesTab';

type Tab = 'otp' | 'categories';

const AssetManagementSettings: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [activeTab, setActiveTab] = useState<Tab>('otp');
  const [requireOtp, setRequireOtp] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await getAssetSettings();
        if (r.errors.length === 0) {
          setRequireOtp(r.payload.requireAssetIssuanceOtp);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onToggle = async (next: boolean) => {
    const prev = requireOtp;
    setRequireOtp(next);
    setSaving(true);
    try {
      const r = await updateAssetSettings({ requireAssetIssuanceOtp: next });
      if (r.errors.length !== 0) {
        setRequireOtp(prev);
        showToast(r.errors[0]?.description ?? t('Update failed'), 'error');
      } else {
        showToast(t('assetSettings.saved'), 'success');
      }
    } catch {
      setRequireOtp(prev);
      showToast(t('Update failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title={t('assetSettings.title')}
      description={t('assetSettings.otpExplain')}
      icon={Laptop}
      accentColor="amber"
    >
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
        <TabsList className="mb-6">
          <TabsTrigger value="otp" className="flex items-center gap-2">
            <Icon name="ShieldCheck" size={16} />
            {t('assetSettings.otpTab')}
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Icon name="Tag" size={16} />
            {t('assetSettings.categoriesTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="otp">
          {loading ? (
            <div className="flex justify-center p-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-start gap-4">
                <Switch
                  checked={requireOtp}
                  onCheckedChange={onToggle}
                  disabled={saving}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-base font-semibold text-foreground">
                    {t('assetSettings.requireOtpLabel')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t('assetSettings.otpExplain')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories">
          <AssetCategoriesTab />
        </TabsContent>
      </Tabs>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default AssetManagementSettings;
